<template>
	<b-modal v-model="isActive" has-modal-card @close="closeModal()">
		<div class="modal-card modalCardContainer" style="
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
			">
			<header class="modal-card-head has-darker-grey-color">
				Tagebuch exportieren{{ clientName ? " — " + clientName : "" }}
			</header>
			<section class="modal-card-body" style="padding: 1em; position: relative">
				<b-loading :is-full-page="false" v-model="isLoading"></b-loading>

				<b-field grouped>
					<b-field label="Von" expanded>
						<b-datepicker v-model="fromDate" :max-date="maxDate" placeholder="Beginn"
							icon="calendar-today" :date-formatter="formatPickerDate" trap-focus>
						</b-datepicker>
					</b-field>
					<b-field label="Bis" expanded>
						<b-datepicker v-model="toDate" :min-date="fromDate" :max-date="maxDate"
							icon="calendar-today" :date-formatter="formatPickerDate" trap-focus>
						</b-datepicker>
					</b-field>
				</b-field>
				<p class="help is-danger" v-if="hasInvalidRange">
					"Von" muss vor "Bis" liegen.
				</p>

				<hr class="sectionDivider" v-if="attributeGroups.length > 0" />

				<div class="is-flex is-align-items-center mb-2"
					v-if="attributeGroups.length > 0">
					<b-button size="is-small" class="mr-1" @click="selectAllAttributes"
						id="selectAllAttributesButton">
						Alle auswählen
					</b-button>
					<b-button size="is-small" @click="deselectAllAttributes"
						:disabled="selectedAttributes.length === 0"
						id="deselectAllAttributesButton">
						Alle abwählen
					</b-button>
				</div>
				<div v-for="group in attributeGroups" :key="group.config.id" class="mb-2">
					<h4 class="attribute_titel mb-1">
						{{ group.config.attributes.name }}
					</h4>
					<div class="is-flex is-flex-wrap-wrap">
						<b-checkbox v-model="selectedAttributes" :native-value="attribute"
							v-for="attribute in group.attributes" :key="attribute.id"
							class="mr-4 mb-1">
							{{ attribute.attributes.name }}
						</b-checkbox>
					</div>
				</div>
				<p class="help" v-if="attributeGroups.length > 0">
					Keine Auswahl = alle Einträge.
				</p>

				<hr class="sectionDivider" />

				<div>
					<b-radio v-model="sortOrder" native-value="asc">
						Älteste zuerst
					</b-radio>
					<b-radio v-model="sortOrder" native-value="desc">
						Neueste zuerst
					</b-radio>
				</div>

				<hr class="sectionDivider" />

				<div>
					<b-checkbox v-model="includePictures" class="mr-4"
						id="includePicturesCheckbox">
						Bilder einbetten
					</b-checkbox>
					<b-checkbox v-model="includePdfs" id="includePdfsCheckbox">
						PDF-Dokumente anhängen
					</b-checkbox>
				</div>
				<p class="help">Anhänge werden nur in den PDF-Export übernommen.</p>
			</section>
			<footer class="modal-card-foot footerContainer">
				<b-button @click="closeModal()">
					Abbrechen
				</b-button>
				<div>
				<b-button icon-left="download" type="is-primary" @click="exportPdf" :disabled="isExportDisabled"
					id="exportPdfButton">
					PDF
				</b-button>
				<b-button icon-left="download" type="is-primary" @click="exportMarkdown" :disabled="isExportDisabled"
					id="exportMarkdownButton" class="ml-2">
					Text
				</b-button>
				</div>
			</footer>
		</div>
	</b-modal>
</template>

<script>
/**
 * Modal for exporting a range of diary entries of the focused client
 * as one PDF or one Markdown file. Filters: date range (defaults to
 * the month window selected in the list view) and entry attributes.
 * @displayName DiaryExportModal
 */

import Parse from "parse/dist/parse.min.js";
import { mapGetters } from "vuex";
import { fetchEntryLinksForExport } from "@/lib/export/fetchEntriesForExport";
import { printDiaryRangePdf } from "@/lib/export/DiaryRangePdf";
import {
	buildDiaryMarkdown, downloadTextFile,
} from "@/lib/export/DiaryMarkdown";
import { exportFilename } from "@/lib/export/exportHelpers";
import { markEntryLinksRead } from "@/lib/export/markEntriesRead";

export default {
	name: "DiaryExportModal",
	props: ["value"],
	data() {
		return {
			fromDate: undefined,
			toDate: undefined,
			attributeGroups: [],
			selectedAttributes: [],
			sortOrder: "asc",
			includePictures: false,
			includePdfs: false,
			isLoading: false,
			maxDate: new Date(),
		};
	},
	computed: {
		...mapGetters({
			focusedClient: "clients/getFocusedClient",
			months: "getChartMonths",
			exportedBy: "usermetas/getUserFullName",
		}),
		isActive: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		clientName() {
			if (!this.focusedClient) { return ""; }
			const attrs = this.focusedClient.attributes;
			return [ attrs.first_name, attrs.last_name ]
				.filter(Boolean).join(" ");
		},
		hasInvalidRange() {
			return this.fromDate && this.toDate &&
				this.fromDate > this.toDate;
		},
		isExportDisabled() {
			return this.isLoading || this.hasInvalidRange;
		},
	},
	watch: {
		value(isOpen) {
			if (isOpen) {
				this.initializeDefaults();
				this.loadAttributeCatalog();
			}
		},
	},
	methods: {
		/*
			The attributeconfigs store subscription loads the configs
			without including their entryAttributes pointers, so the
			attribute names are only available when some other query
			happened to fetch the same objects. Fetch the catalog
			explicitly instead.
		*/
		async loadAttributeCatalog() {
			try {
				const query = new Parse.Query("AttributeConfig");
				query.notEqualTo("deleted", true);
				query.include("entryAttributes");
				query.limit(1000);
				const configs = await query.find();
				this.attributeGroups = configs
					.filter((config) => config.attributes.entryAttributes &&
						config.attributes.entryAttributes.length > 0)
					.map((config) => {
						return {
							config,
							attributes:
								config.attributes.entryAttributes.filter(
									(attribute) =>
										attribute.attributes.deleted !== true
								),
						};
					})
					.filter((group) => group.attributes.length > 0);
			}
			catch (error) {
				this.attributeGroups = [];
			}
		},
		selectAllAttributes() {
			this.selectedAttributes = this.attributeGroups.flatMap(
				(group) => group.attributes
			);
		},
		deselectAllAttributes() {
			this.selectedAttributes = [];
		},
		initializeDefaults() {
			this.maxDate = new Date();
			this.toDate = new Date();
			if (this.months) {
				const from = new Date();
				from.setMonth(from.getMonth() - this.months);
				this.fromDate = from;
			}
			else {
				// "Alle": start at the client's Hilfebeginn if known,
				// otherwise leave open (no lower bound).
				this.fromDate = this.focusedClient &&
					this.focusedClient.attributes.entry
					? new Date(this.focusedClient.attributes.entry)
					: undefined;
			}
			this.selectedAttributes = [];
			this.sortOrder = "asc";
			this.includePictures = false;
			this.includePdfs = false;
		},
		formatPickerDate(date) {
			return date.toLocaleDateString("de-DE", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
			});
		},
		async exportPdf() {
			await this.runExport("pdf");
		},
		async exportMarkdown() {
			await this.runExport("md");
		},
		async runExport(kind) {
			if (!this.focusedClient) { return; }
			this.isLoading = true;
			try {
				const attributeNames = this.selectedAttributes.map(
					(attribute) => attribute.attributes.name
				);
				const options = {
					client: this.focusedClient,
					from: this.fromDate,
					to: this.toDate,
					attributeNames,
					exportedBy: this.exportedBy,
					includePictures: this.includePictures,
					includePdfs: this.includePdfs,
				};
				const entryLinks = await fetchEntryLinksForExport({
					clientId: this.focusedClient.id,
					from: this.fromDate,
					to: this.toDate,
					attributeFilter: {
						ids: this.selectedAttributes.map((a) => a.id),
						names: attributeNames,
					},
					sortOrder: this.sortOrder,
				});
				if (entryLinks.length === 0) {
					this.$buefy.toast.open({
						message: "Keine Einträge im gewählten Zeitraum.",
						type: "is-warning",
						position: "is-bottom",
					});
					return;
				}
				options.entryLinks = entryLinks;
				if (kind === "pdf") {
					await printDiaryRangePdf(options);
				}
				else {
					downloadTextFile(
						exportFilename(this.focusedClient, this.fromDate,
							this.toDate, "md"),
						buildDiaryMarkdown(options)
					);
				}
				// The export delivered the full content, so every
				// exported entry counts as read. Failing to store the
				// receipts must not fail the export itself.
				try {
					await markEntryLinksRead(entryLinks,
						this.$store.state.currentUser);
				}
				catch (error) {
					this.$buefy.toast.open({
						message: "Einträge konnten nicht als gelesen " +
							"markiert werden: " + error.toString(),
						type: "is-warning",
						position: "is-bottom",
					});
				}
				this.isActive = false;
			}
			catch (error) {
				this.$buefy.toast.open({
					message: "Export fehlgeschlagen: " + error.toString(),
					type: "is-danger",
					position: "is-bottom",
				});
			}
			finally {
				this.isLoading = false;
			}
		},
		closeModal() {
			this.isActive = false;
		},
	},
};
</script>

<style scoped>
.modalCardContainer {
	max-height: 90vh;
	width: 34em;
	max-width: 95vw;
	box-shadow: 0 8px 40px rgba(0, 0, 0, 0.55);
	border-radius: 8px;
	overflow: hidden;
}

/*
	The default Buefy overlay (rgba(10,10,10,.86)) is so dark that the
	dark modal chrome visually merges with it -- dim less and let the
	card shadow do the separation.
*/
::v-deep .modal-background {
	background-color: rgba(10, 10, 10, 0.45);
}

/*
	b-modal renders in place (no teleport), so this modal inherits the
	white text color of the dark top bar it is mounted in. Reset to the
	regular Bulma body color for everything inside the card body.
*/
.modal-card-body {
	color: #4a4a4a;
}

/*
	ClientHeader (which this modal is mounted in) paints all .mdi
	icons whitesmoke for the dark top bar; that bleeds into the modal.
	More specific selector => icons take their surrounding color again.
*/
::v-deep .modal-card .mdi {
	color: inherit;
}

.sectionDivider {
	margin: 0.9em 0;
	height: 1px;
	background-color: #dbdbdb;
}

.attribute_titel {
	font-weight: 600;
}
</style>
